import './style.css'
import { composeLayerData } from './lib/map-state'
import { createLayerStateTracker } from './lib/layer-state'
import { uniqueRegions, uniqueSources } from './lib/filters'
import { createMap, type MapController } from './map'
import { onColorSchemeChange } from './lib/theme'
import { createUI, fsdMarketDetailHtml, segmentDetailHtml, takeoverDetailHtml } from './ui'
import type { DataMetadata, SegmentCollection, TakeoverCollection } from './lib/types'
import type { LayerState } from './lib/layer-state'
import type { RenderedProof } from './lib/rendered-proof'
import type { FsdAvailabilityCollection } from './lib/fsd-availability'
import type { VerifyFixture } from './lib/verify-fixture'

declare global {
  interface Window {
    __TRUST_FSD_READY__?: boolean
    __TRUST_FSD_STATS__?: Record<string, number>
    __TRUST_FSD_VERIFY__?: boolean
    __TRUST_FSD_MAP__?: {
      getLayerState: () => LayerState
      getRenderedProof: () => RenderedProof
      isMapIdle: () => boolean
      waitForIdle: () => Promise<void>
      focusCoverage: () => void
      focusC2CCorridor: () => void
    }
  }
}

interface LoadedBundle {
  segments: SegmentCollection
  takeover: TakeoverCollection
  metadata: DataMetadata & { center?: [number, number]; zoom?: number }
}

async function loadBundle(verifyMode: boolean): Promise<LoadedBundle> {
  const [segmentsRes, metadataRes, takeoverRes, fixtureRes] = await Promise.all([
    fetch('/data/segments.json'),
    fetch('/data/metadata.json'),
    fetch('/data/takeover-clustered.json'),
    verifyMode ? fetch('/data/verify-fixture.json') : Promise.resolve(null),
  ])
  if (!segmentsRes.ok || !metadataRes.ok || !takeoverRes.ok) {
    throw new Error('Missing bundled data — run npm run ingest')
  }

  const metadata: DataMetadata = await metadataRes.json()

  if (verifyMode) {
    if (!fixtureRes?.ok) throw new Error('Missing verify-fixture.json — run npm run ingest')
    const fixture: VerifyFixture = await fixtureRes.json()
    return {
      segments: fixture.segments,
      takeover: fixture.takeover,
      metadata: {
        ...metadata,
        bounds: fixture.metadata.bounds,
        c2cBounds: fixture.metadata.c2cBounds,
        center: fixture.metadata.center,
        zoom: fixture.metadata.zoom,
        segmentCount: fixture.segments.features.length,
        takeoverPointCount: fixture.takeover.features.length,
      },
    }
  }

  return {
    segments: await segmentsRes.json(),
    takeover: await takeoverRes.json(),
    metadata,
  }
}

async function init() {
  const verifyMode = new URLSearchParams(location.search).has('verify')
  window.__TRUST_FSD_VERIFY__ = verifyMode

  const mapEl = document.getElementById('map')!
  const overlay = document.createElement('div')
  overlay.className = 'loading-overlay'
  overlay.innerHTML = '<span class="loading-mark" aria-hidden="true"></span><span>Loading community coverage…</span>'
  mapEl.parentElement?.appendChild(overlay)

  const [bundle, fsdMarketsRes] = await Promise.all([
    loadBundle(verifyMode),
    fetch('/data/fsd-availability.json'),
  ])
  overlay.remove()

  if (!fsdMarketsRes.ok) throw new Error('Missing fsd-availability.json — run npm run ingest')
  const fsdMarkets: FsdAvailabilityCollection = await fsdMarketsRes.json()

  const ui = createUI(uniqueRegions(bundle.segments), uniqueSources(bundle.segments))
  ui.renderLegend()
  ui.renderAttribution(bundle.metadata.sources.map((s) => ({ id: s.id, label: s.label })))

  const layerState = createLayerStateTracker()
  const mapCtrl = createMap(mapEl, bundle.metadata.bounds, { verifyMode })
  onColorSchemeChange((scheme) => mapCtrl.setColorScheme(scheme))

  function publishStats(state: LayerState) {
    window.__TRUST_FSD_STATS__ = {
      routes: bundle.metadata.routeCount,
      coordinates: bundle.metadata.coordinateCount,
      segments: state.segmentFeatures,
      takeover: state.takeoverFeatures,
      undriven: state.undrivenFeatures,
    }
  }

  function applyFilters(ctrl: MapController) {
    const composed = composeLayerData(
      { segments: bundle.segments, takeover: bundle.takeover },
      ui.getFilters(),
    )
    const filters = ui.getFilters()
    ctrl.setSegments(composed.segments)
    ctrl.setTakeover(composed.takeover)
    ctrl.setFsdMarketsVisible(filters.showFsdMarkets)
    const state = layerState.apply(composed)
    publishStats(state)
    ui.updateStats({
      segments: state.segmentFeatures,
      routes: bundle.metadata.routeCount,
      coords: bundle.metadata.coordinateCount,
      takeover: state.takeoverFeatures,
    })
  }

  function focusC2CCorridor(ctrl: MapController) {
    if (verifyMode && bundle.metadata.zoom) {
      const b = bundle.metadata.c2cBounds ?? bundle.metadata.bounds
      const center: [number, number] = [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2]
      ctrl.map.jumpTo({ center, zoom: bundle.metadata.zoom })
      return
    }
    const b = bundle.metadata.c2cBounds ?? bundle.metadata.bounds
    const center: [number, number] = [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2]
    ctrl.map.jumpTo({ center, zoom: 5 })
  }

  window.__TRUST_FSD_MAP__ = {
    getLayerState: () => layerState.get(),
    getRenderedProof: () => mapCtrl.getRenderedProof(),
    isMapIdle: () => mapCtrl.isMapIdle(),
    waitForIdle: () => mapCtrl.waitForIdle(),
    focusCoverage: () => focusC2CCorridor(mapCtrl),
    focusC2CCorridor: () => focusC2CCorridor(mapCtrl),
  }

  await mapCtrl.whenLayersReady()
  mapCtrl.setFsdMarkets(fsdMarkets)
  applyFilters(mapCtrl)
  focusC2CCorridor(mapCtrl)
  await new Promise<void>((resolve) => {
    mapCtrl.map.once('idle', () => resolve())
  })
  window.__TRUST_FSD_READY__ = true

  ui.onFilterChange(() => applyFilters(mapCtrl))

  mapCtrl.onSegmentClick((props) => {
    ui.showDetail(segmentDetailHtml(props))
  })

  mapCtrl.onTakeoverClick((props) => {
    ui.showDetail(takeoverDetailHtml(props))
  })

  mapCtrl.onFsdMarketClick((props) => {
    ui.showDetail(fsdMarketDetailHtml(props))
  })
}

init().catch((err) => {
  console.error('Trust FSD init failed:', err)
  const mapEl = document.getElementById('map')
  if (mapEl) {
    mapEl.innerHTML = `<p style="padding:24px;color:#f87171">Failed to load map data: ${err.message}</p>`
  }
})